import type { ReactNode } from 'react'

import {
  TextLoader,
  EmptyState,
  InfoAlert,
  Space,
  Tag,
  TertiaryButton,
  Text,
} from '@procurify/ui'

import { FormattedShortDate } from '@webapp/core/components/FormattedDate'
import { PaginationControls } from '@webapp/core/components/PaginationControls'
import { useProcurifyIntl } from '@webapp/hooks'
import { type StatementItem } from '@webapp/modules/accounts-payable/entities/StatementItem'
import messages from './messages'
import { SelectableRowStyled } from './styles/SelectableRowStyled'

export interface IUnmatchedStatementItemsPaneProps {
  items: StatementItem[]
  isLoading: boolean
  selectedItemIds: number[]
  /** "JUST CREATED" flags — keyed by item id so they survive refetch (BHV-041/042) */
  justCreatedItemIds: number[]
  /** True while the suggested-match filter narrows the pane (BHV-037) */
  isFiltered: boolean
  onClearFilter: () => void
  onRowClick: (item: StatementItem) => void
  /** Slot for the Duplicate / Remove menu — hidden for matched rows (BHV-042) */
  renderRowMenu: (item: StatementItem) => ReactNode
}

export const UnmatchedStatementItemsPane = ({
  items,
  isLoading,
  selectedItemIds,
  justCreatedItemIds,
  isFiltered,
  onClearFilter,
  onRowClick,
  renderRowMenu,
}: IUnmatchedStatementItemsPaneProps) => {
  const intl = useProcurifyIntl()

  return (
    <PaginationControls id='ap-reconcile-unmatched-statement-items'>
      <Space>
        <Text bold autoMargin={false}>
          {intl.formatMessage(messages.paneTitle)}
        </Text>

        {/* Per-pane search + sort (BHV-052) */}
        <Space direction='horizontal' justify='space-between' align='center'>
          <Space.Item minWidth={240}>
            <PaginationControls.Search
              placeholder={intl.formatMessage(messages.searchPlaceholder)}
            />
          </Space.Item>
          <Space.Item minWidth={160}>
            <PaginationControls.Sort
              sortKeys={[
                {
                  label: intl.formatMessage(messages.sortDateLabel),
                  value: 'date',
                },
                {
                  label: intl.formatMessage(messages.sortTotalCostLabel),
                  value: 'amount',
                },
                {
                  label: intl.formatMessage(messages.sortDescriptionLabel),
                  value: 'description',
                },
              ]}
            />
          </Space.Item>
        </Space>

        {/* Suggested-match filter banner (BHV-037) */}
        {isFiltered && (
          <InfoAlert>
            <Space direction='horizontal' align='center' size='small'>
              <Text autoMargin={false}>
                {intl.formatMessage(messages.suggestedFilterBanner)}
              </Text>
              <TertiaryButton type='button' onClick={onClearFilter}>
                {intl.formatMessage(messages.clearFilterButton)}
              </TertiaryButton>
            </Space>
          </InfoAlert>
        )}

        {isLoading && <TextLoader />}

        {!isLoading && items.length === 0 && (
          <EmptyState header={intl.formatMessage(messages.emptyStateHeader)} />
        )}

        {!isLoading && items.length > 0 && (
          <div>
            {items.map((item) => {
              const isSelected = selectedItemIds.includes(item.id)

              return (
                <SelectableRowStyled
                  key={item.id}
                  $selected={isSelected}
                  onClick={() => onRowClick(item)}
                  role='button'
                  aria-pressed={isSelected}
                  aria-label={intl.formatMessage(messages.selectRowAriaLabel, {
                    description: item.description,
                  })}
                >
                  <Space size='small'>
                    <Space
                      direction='horizontal'
                      justify='space-between'
                      align='center'
                    >
                      <Space direction='horizontal' size='small' align='center'>
                        <Text bold autoMargin={false}>
                          {item.description}
                        </Text>
                        {justCreatedItemIds.includes(item.id) && (
                          <Tag>
                            {intl.formatMessage(messages.justCreatedTag)}
                          </Tag>
                        )}
                      </Space>
                      {/* Overflow hidden for matched items (BHV-042) */}
                      {!item.isMatched && (
                        <div onClick={(event) => event.stopPropagation()}>
                          {renderRowMenu(item)}
                        </div>
                      )}
                    </Space>
                    <Space
                      direction='horizontal'
                      justify='space-between'
                      align='center'
                    >
                      <Text autoMargin={false}>
                        <FormattedShortDate
                          date={new Date(`${item.date}T00:00:00`)}
                        />
                      </Text>
                      <Text bold autoMargin={false}>
                        {item.amountValue.toFixed(2)}
                      </Text>
                    </Space>
                  </Space>
                </SelectableRowStyled>
              )
            })}
          </div>
        )}

        <Space direction='horizontal' justify='end' align='center'>
          <PaginationControls.Page />
        </Space>
      </Space>
    </PaginationControls>
  )
}

UnmatchedStatementItemsPane.displayName = 'UnmatchedStatementItemsPane'
